import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { Component, ElementRef, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { Answer } from '@digilize/shared/definitions/src';
import { stripHTML, getFirstControlWithError } from '@digilize/shared/utils/helpers/src';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'lib-multi-dropdown-autocomplete',
  templateUrl: './multi-dropdown-autocomplete.component.html',
  styleUrls: ['./multi-dropdown-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MultiDropdownAutocompleteComponent implements OnInit {
  _answers: AnswerWithOtherChild[];
  @Input() set answers(answers: AnswerWithOtherChild[]) {
    this._answers = answers
      .map((answer) => ({ ...answer, text: stripHTML(answer.text) }))
      .filter((answer) => !answer.is_other);
  }
  /* This component handles group of boolean controls + extra */
  @Input() formGroup: FormGroup = new FormGroup({ mockControl1: new FormControl(), mockControl2: new FormControl() });
  @Input() othersFg: FormGroup = new FormGroup({
    mockControl1: new FormControl(),
  });
  @Input() placeholder: string;
  /** Show the placeholder as a floating label, as Material's legacy field did with a placeholder. */
  @Input() placeholderAsLabel = false;
  @ViewChild('autoInput') autoInput: ElementRef;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  filterAnswers: Observable<AnswerWithOtherChild[]>;
  filterString = '';
  autoCompleteControl: FormControl;
  chipList$: Observable<AnswerWithOtherChild[]>;
  public getFirstControlWithError = getFirstControlWithError;
  constructor() {}
  ngOnInit(): void {
    this.setControl();
  }

  setControl() {
    this.chipList$ = combineLatest([
      this.formGroup.valueChanges.pipe(startWith(this.formGroup.value)),
      this.othersFg.valueChanges.pipe(startWith(this.othersFg.value)),
    ]).pipe(
      map(([checkboxes, others]: [Record<string, boolean>, Record<string, string>]) => {
        const selectedAnswers: AnswerWithOtherChild[] = Object.entries(checkboxes)
          .filter(([id, value]) => !!value)
          .map(([id]) => this._answers.find((answer) => answer.val === id));
        const selectedOthers: AnswerWithOtherChild[] = Object.entries(others)
          .filter(([id, value]) => !!value)
          .map(([id, value]) => ({ val: id, text: value, is_other: true, otherChild: true, tooltip: null }));
        return [...selectedAnswers, ...selectedOthers];
      })
    );

    this.autoCompleteControl = new FormControl();
    this.filterAnswers = this.autoCompleteControl.valueChanges.pipe(
      startWith<string>(''),
      map((value) => (typeof value === 'string' ? value : this.filterString)),
      map((filter) => this.filter(filter))
    );
  }

  filter = (filter: string): AnswerWithOtherChild[] => {
    if (filter.length <= 0) {
      return [...this._answers];
    }

    return this._answers.filter((option) => {
      return option.text.toLowerCase().indexOf(filter.toLowerCase()) >= 0;
    });
  };

  handleLabelClick(event: Event) {
    event.stopPropagation();
    setTimeout(() => {
      this.autoInput.nativeElement.focus();
      this.autoCompleteControl.setValue('');
      this.formGroup.controls[Object.keys(this.formGroup.controls)[0]].updateValueAndValidity();
    }, 0);
  }

  removeChip = (data: AnswerWithOtherChild): void => {
    if (data.otherChild) {
      this.othersFg.markAllAsTouched();
      this.othersFg.controls[data.val].setValue(null);
      this.othersFg.controls[Object.keys(this.othersFg.controls)[0]].updateValueAndValidity();
    } else {
      this.formGroup.markAllAsTouched();
      this.formGroup.controls[data.val].setValue(false);
      this.formGroup.controls[Object.keys(this.formGroup.controls)[0]].updateValueAndValidity();
    }
  };

  addExtra(e: MatChipInputEvent) {
    this.othersFg.controls.le;
    const emptyOtherControls = Object.entries(this.othersFg.controls).filter(([key, control]) => !control.value);
    if (emptyOtherControls.length === 0) return;
    const firstEmptyControlKey = emptyOtherControls[0][0];
    this.othersFg.controls[firstEmptyControlKey].setValue(e.value);
    this.autoCompleteControl.setValue('');
  }
}

type AnswerWithOtherChild = Answer & { otherChild?: boolean };
